import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { logger } from "@/lib/logger";
import { getSupabase } from "../utils";
import { useConnections as useConnectionsFromContext } from "@/lib/contexts/ConnectionsContext";
import { fetchMessages } from "./api-client";
import {
  buildAccessibleContactsFromGrants,
  buildClientPortalGrantSummary,
  type CompanyGrantRow,
  type ContactGrantRow,
  type RlsContactRow,
} from "@/lib/auth/client-portal-grants";
import type {
  AccessibleClientsResult,
  MessagesQueryParams,
} from "./types";
import {
  buildRecurringBillingSummary,
  type RecurringBillingSummary,
  type RecurringChargeRow,
  type RecurringTransactionRow,
} from "./recurring-billing-shape";

// Client Portal — accessible contacts + grant summary (contact + company grants)
export function useAccessibleClients(enabled: boolean = true) {
  return useQuery({
    queryKey: ["client-portal", "accessible-clients"],
    enabled,
    queryFn: async (): Promise<AccessibleClientsResult> => {
      const supabase = getSupabase();
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        throw new Error("User not authenticated");
      }

      const [contactGrantsRes, companyGrantsRes, contactsRes] = await Promise.all([
        supabase
          .from("contact_user_access")
          .select(
            `
            contact_id,
            access_level,
            is_active,
            expires_at,
            contacts:contact_id (
              id,
              name,
              company_name,
              company_id
            )
          `
          )
          .eq("user_id", user.id),
        supabase
          .from("company_user_access")
          .select(
            `
            company_id,
            access_level,
            is_active,
            expires_at,
            companies:company_id (
              id,
              name
            )
          `
          )
          .eq("user_id", user.id),
        supabase
          .from("contacts")
          .select("id, name, company_name, company_id")
          .eq("is_active", true)
          .order("name"),
      ]);

      if (contactGrantsRes.error) {
        logger.error("Error fetching contact_user_access grants", contactGrantsRes.error);
        throw new Error(contactGrantsRes.error.message);
      }
      if (companyGrantsRes.error) {
        logger.error("Error fetching company_user_access grants", companyGrantsRes.error);
        throw new Error(companyGrantsRes.error.message);
      }
      if (contactsRes.error) {
        logger.error("Error fetching RLS-scoped contacts", contactsRes.error);
        throw new Error(contactsRes.error.message);
      }

      const contactGrants = (contactGrantsRes.data ?? []) as unknown as ContactGrantRow[];
      const companyGrants = (companyGrantsRes.data ?? []) as unknown as CompanyGrantRow[];
      const rlsContacts = (contactsRes.data ?? []) as RlsContactRow[];

      const grantSummary = buildClientPortalGrantSummary(contactGrants, companyGrants);
      const accessibleContacts = buildAccessibleContactsFromGrants(
        contactGrants,
        companyGrants,
        rlsContacts
      );

      return {
        accessibleContacts,
        hasAccess: grantSummary.hasAccess,
        grantSummary,
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}

// Client Portal — the caller's own recurring billing items (item-level), from
// the two client-safe views. RLS on the underlying tables (has_company_access)
// is what actually restricts rows to the caller's own company — this hook
// does not add its own filter and must not, so a widened RLS grant is the only
// way scope ever changes here (see the migration's cross-tenant verification
// note before promoting past hub-dev).
export function useMyRecurringBilling(enabled: boolean = true) {
  return useQuery({
    queryKey: ["client-portal", "recurring-billing"],
    enabled,
    queryFn: async (): Promise<RecurringBillingSummary> => {
      const supabase = getSupabase();
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        throw new Error("User not authenticated");
      }

      const [transactionsRes, chargesRes] = await Promise.all([
        supabase.from("client_portal_recurring_transactions").select("*"),
        supabase.from("client_portal_recurring_charges").select("*"),
      ]);

      if (transactionsRes.error) {
        logger.error(
          "Error fetching client_portal_recurring_transactions",
          transactionsRes.error
        );
        throw new Error(transactionsRes.error.message);
      }
      if (chargesRes.error) {
        logger.error("Error fetching client_portal_recurring_charges", chargesRes.error);
        throw new Error(chargesRes.error.message);
      }

      const transactions = (transactionsRes.data ?? []) as unknown as RecurringTransactionRow[];
      const charges = (chargesRes.data ?? []) as unknown as RecurringChargeRow[];

      return buildRecurringBillingSummary(transactions, charges);
    },
    staleTime: 5 * 60 * 1000,
  });
}

// the provider Connections - Now using centralized context
export function useConnections() {
  const { connections, connectionsLoading, connectionsError, refetchConnections } =
    useConnectionsFromContext();
  return {
    data: connections,
    isLoading: connectionsLoading,
    error: connectionsError ? new Error(connectionsError) : null,
    refetch: refetchConnections,
  };
}

// the provider Inboxes - Now using centralized context
export function useInboxes() {
  const { inboxes, inboxGroups, inboxesLoading, inboxesError, refetchConnections } =
    useConnectionsFromContext();
  // `data` is a dependency for consumers; build it only when its parts change.
  const data = useMemo(() => ({ inboxes, groups: inboxGroups }), [inboxes, inboxGroups]);
  return {
    data,
    isLoading: inboxesLoading,
    error: inboxesError ? new Error(inboxesError) : null,
    refetch: refetchConnections,
  };
}

// the provider Messages
export function useMessages(
  provider: string,
  params: MessagesQueryParams,
  enabled: boolean = true
) {
  const { session } = useUniversalAuth();

  return useQuery({
    queryKey: ["messages", provider, params],
    queryFn: async () => {
      if (!session?.access_token) {
        throw new Error("No authentication token available");
      }
      return fetchMessages(provider, params, session.access_token);
    },
    enabled: enabled && !!session?.access_token,
    staleTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
  });
}
